import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as whatsappApi from './whatsapp.api';

import { extractServerError } from '@/lib/api-error';

const QK = {
  inbox: () => ['whatsapp', 'inbox'] as const,
  thread: (id: string) => ['whatsapp', 'thread', id] as const,
  templates: () => ['whatsapp', 'templates'] as const,
  templatesManage: () => ['whatsapp', 'templates', 'manage'] as const,
};

/** Sendable templates (Live campaigns only). Server caches ~5 min; match it. */
export const useWhatsAppTemplates = (enabled = true) => {
  return useQuery({
    queryKey: QK.templates(),
    queryFn: whatsappApi.listWhatsAppTemplates,
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};

/**
 * Inbox list drives the widget badge, so it polls even while the panel is
 * closed - kept cheap server-side (single list query + previews).
 */
export const useWhatsAppInbox = (enabled: boolean) => {
  return useQuery({
    queryKey: QK.inbox(),
    queryFn: whatsappApi.listWhatsAppConversations,
    enabled,
    refetchInterval: 20_000,
    retry: 1,
  });
};

/** Open thread polls faster - the operator is actively chatting. */
export const useWhatsAppThread = (conversationId: string | null) => {
  return useQuery({
    queryKey: QK.thread(conversationId ?? 'none'),
    queryFn: () => whatsappApi.getWhatsAppThread(conversationId!),
    enabled: conversationId !== null,
    refetchInterval: 8_000,
    retry: 1,
  });
};

export const useSendWhatsApp = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      conversationId,
      payload,
    }: {
      conversationId: string;
      payload: whatsappApi.SendWhatsAppPayload;
    }) => whatsappApi.sendWhatsAppMessage(conversationId, payload),
    onSuccess: (_r, variables) => {
      void queryClient.invalidateQueries({ queryKey: QK.thread(variables.conversationId) });
      void queryClient.invalidateQueries({ queryKey: QK.inbox() });
    },
    // Deliberately silent: a delivery failure is not something the agent can
    // act on, and the admin WhatsApp log carries it with the reason attached.
    onError: (err: unknown) => console.warn('WhatsApp send failed', err),
  });
};

/** ADMIN: all approved templates + hidden flags for the manage screen. */
export const useWhatsAppTemplatesManage = (enabled: boolean) => {
  return useQuery({
    queryKey: QK.templatesManage(),
    queryFn: whatsappApi.listWhatsAppTemplatesManage,
    enabled,
    retry: 1,
  });
};

export const useSetWhatsAppTemplateHidden = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ name, hidden }: { name: string; hidden: boolean }) =>
      whatsappApi.setWhatsAppTemplateHidden(name, hidden),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QK.templatesManage() });
      void queryClient.invalidateQueries({ queryKey: QK.templates() });
    },
    onError: (err: unknown) =>
      toast.error(extractServerError(err).message ?? 'Could not update the template'),
  });
};
